<template>
  <div class="g-body-width py-6 overflow-y-auto flex-1 min-h-0">
    <div class="flex gap-2 flex-1 px-4">
      <!-- 左列：发音按钮 -->
      <div class="flex-1 flex flex-col gap-4 overflow-hidden">
        <Button
          v-for="(item, i) in pronunciations"
          :key="i"
          :scene="getButtonScene(i, selectedPronunciationIndex)"
          :disabled="completedPronunciation.has(i)"
          :class="['option']"
          @click="onPronunciationSelect(i)"
        >
          <div class="flex justify-center items-center h-full w-full">
            <LottieBox
              :ref="
                el => {
                  if (el) waveLotties[i] = el
                }
              "
              name="wave"
              class="h-full w-full"
              :autoplay="false"
              loop
            />
          </div>
        </Button>
      </div>

      <!-- 右列：汉字 -->
      <div class="flex-1 flex flex-col gap-4 overflow-hidden">
        <Button
          v-for="(item, i) in hanzis"
          :key="i"
          :scene="getButtonScene(i, selectedHanziIndex)"
          :disabled="completedHanzi.has(i)"
          :class="['option']"
          @click="onHanziSelect(i)"
        >
          <div
            class="flex justify-center items-center h-full w-full text-48px font-bold"
          >
            {{ item }}
          </div>
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type VirtualCardKidHanziMatch } from '@/types/core'
import { FeedbackStar } from '@/components/ConcreteCard/common'
import { ref, onMounted } from 'vue'
import { UnitEventType } from '@/types/core'
import { shuffle } from 'lodash-es'
import type { ButtonScene } from '../Button.vue'
import { vibrate, VibrateType } from '@/utils/misc'
import LottieBox from '@/components/LottieBox.vue'

const props = defineProps<{
  match: VirtualCardKidHanziMatch
}>()

const emit = defineEmits<{
  close: []
  star: [star: FeedbackStar]
  event: [undefined, UnitEventType]
  correct: []
  done: []
}>()

// 状态管理
const pronunciations = ref<string[]>([])
const hanzis = ref<string[]>([])
const waveLotties = ref<any[]>([])

const selectedPronunciationIndex = ref<number>()
const selectedHanziIndex = ref<number>()
const completedPronunciation = ref<Set<number>>(new Set())
const completedHanzi = ref<Set<number>>(new Set())
const result = ref<'correct' | 'wrong'>()
const passed = ref(false)
const wrongTimes = ref(0)

// 核心交互逻辑
function onPronunciationSelect(index: number) {
  selectedPronunciationIndex.value = index
  playPronunciation(index)

  if (selectedHanziIndex.value != null) {
    checkAnswer()
  }
}

function onHanziSelect(index: number) {
  selectedHanziIndex.value = index

  if (selectedPronunciationIndex.value != null) {
    checkAnswer()
  }
}

function playPronunciation(index: number) {
  const pinyin = pronunciations.value[index]
  const lottie = waveLotties.value[index]

  lottie?.play()
  _am.playPinyin(pinyin).then(() => {
    lottie?.pause()
  })
}

function checkAnswer() {
  if (
    selectedPronunciationIndex.value == null ||
    selectedHanziIndex.value == null
  )
    return

  const pronunciation = pronunciations.value[selectedPronunciationIndex.value]
  const hanzi = hanzis.value[selectedHanziIndex.value]

  // 找到对应的卡片进行匹配验证
  const hanziCard = props.match.cards.find(
    item => item.hanzi[0][1] === hanzi && item.hanzi[0][2] === pronunciation
  )

  if (hanziCard) {
    result.value = 'correct'
    completedPronunciation.value.add(selectedPronunciationIndex.value)
    completedHanzi.value.add(selectedHanziIndex.value)
    emit('correct')
    vibrate(VibrateType.Success)
  } else {
    result.value = 'wrong'
    wrongTimes.value++
    vibrate(VibrateType.Warning)
  }

  setTimeout(() => {
    result.value = undefined
    selectedPronunciationIndex.value = undefined
    selectedHanziIndex.value = undefined
  }, 200)

  const allDone = completedPronunciation.value.size === props.match.cards.length

  if (allDone) {
    passed.value = true
    const star = wrongTimes.value > 0 ? FeedbackStar.Two : FeedbackStar.Three
    emit('star', star)
    emit('event', undefined, UnitEventType.CORRECT)
  }
}

function getButtonScene(index: number, selectedIndex?: number): ButtonScene {
  if (selectedIndex === index) {
    if (result.value === 'correct') {
      return 'choiceCorrect'
    } else if (result.value === 'wrong') {
      return 'choiceWrong'
    }
    return 'choiceSelected'
  }
  return 'choice'
}

function generateOptions() {
  hanzis.value = shuffle(props.match.cards.map(item => item.hanzi[0][1]))
  pronunciations.value = shuffle(
    props.match.cards.map(item => item.hanzi[0][2])
  )
}

onMounted(async () => {
  // 预加载所有发音资源
  const pinyinList = props.match.cards.map(item => ({
    type: 'pinyin' as const,
    pinyin: item.hanzi[0][2],
  }))
  await _am.preload(pinyinList)

  generateOptions()
})

onInit(generateOptions)
</script>

<style scoped>
.option {
  height: 100px;
}

/* 强制 Lottie SVG 适应容器大小 */
.option :deep(svg) {
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  max-height: none !important;
}
</style>
